import React, { useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import Chart from 'chart.js/auto';
import ReportComponent from '../utils/ReportComponent/ReportComponent';
import Loading from './Loading';

const reportFormatter = ReportComponent.formatter;
const defLeftScale = {
    type: "linear",
    position: "left",
    display: true,
    ticks: {
        maxTicksLimit: 10,
        stepSize: 0.5
    },
    min: 0
};

const defRightScale = {
    type: "linear",
    position: "right",
    display: true,
    ticks: {
        maxTicksLimit: 10,
        stepSize: 0.5
    },
    grid: {
        drawOnChartArea: false
    },
    min: 0
};

function ChartLine(props) {
    const { classNameWrapp, classNameCanvas, height, yAxesLeft, yAxesRight, loading, chartData, parameters } = props;
    const canvasRef = useRef(null);
    const chartRef = useRef(null);
    const [dimensionColors, setDimensionColors] = useState({
        first: '#FD267E',
        second: '#000056'
    });

    useEffect(() => {
        if (typeof window === 'undefined') {
            return;
        }

        const root = document.documentElement;
        const computed = getComputedStyle(root);
        const first = (computed.getPropertyValue('--dimension-first') || '#FD267E').trim() || '#FD267E';
        const second = (computed.getPropertyValue('--dimension-second') || '#000056').trim() || '#000056';
        setDimensionColors({ first, second });
    }, []);
    const options = useMemo(() => ({
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: { display: false },
            tooltip: {
                mode: 'index',
                intersect: false,
                callbacks: {
                    beforeTitle: (items) => {
                        const keys = { 0: yAxesLeft, 1: yAxesRight };
                        items.forEach((item) => {
                            const metricKey = keys[item.datasetIndex];
                            if (typeof item.yLabel !== 'undefined') {
                                item.yLabel = reportFormatter.format(item.yLabel, metricKey, parameters);
                            }
                        });
                    }
                }
            }
        },
        scales: {
            x: { grid: { display: false } }
        }
    }), [yAxesLeft, yAxesRight, parameters]);

    const buildData = () => {
        const data = { labels: [], datasets: [] };
        if (!chartData || !chartData.labels || !chartData.labels.length) return data;
        data.labels = chartData.labels;
        const left = chartData.metricsData[yAxesLeft];
        const right = yAxesRight ? chartData.metricsData[yAxesRight] : null;
        if (left) {
            data.datasets.push({
                label: left.title,
                data: left.data,
                metric: yAxesLeft,
                borderColor: dimensionColors.first,
                backgroundColor: dimensionColors.first,
                yAxisID: 'y',
                tension: 0.3
            });
        }
        if (right) {
            data.datasets.push({
                label: right.title,
                data: right.data,
                metric: yAxesRight,
                borderColor: dimensionColors.second,
                backgroundColor: dimensionColors.second,
                yAxisID: 'y1',
                tension: 0.3
            });
        }

        return data;
    };

    const applyScales = (opts, data) => {
        opts.scales = opts.scales || {};
        if (data.datasets.some(d => d.yAxisID === 'y')) {
            opts.scales.y = {
                ...defLeftScale,
                ticks: {
                    ...defLeftScale.ticks,
                    callback: (tick) => reportFormatter.format(tick, yAxesLeft, parameters)
                }
            };
        }
        if (data.datasets.some(d => d.yAxisID === 'y1')) {
            opts.scales.y1 = {
                ...defRightScale,
                ticks: {
                    ...defRightScale.ticks,
                    callback: (tick) => reportFormatter.format(tick, yAxesRight, parameters)
                }
            };
        } else {
            delete opts.scales.y1;
        }
    };

    useEffect(() => {
        if (parameters) {
            parameters.isHtml = false;
        }
    }, [parameters]);

    useEffect(() => {
        if (!canvasRef.current) return;
        if (chartRef.current) {
            chartRef.current.destroy();
            chartRef.current = null;
        }
        const data = buildData();
        const opts = JSON.parse(JSON.stringify(options));
        applyScales(opts, data);
        chartRef.current = new Chart(canvasRef.current.getContext('2d'), {
            type: 'line',
            data,
            options: opts
        });
        return () => {
            if (chartRef.current) {
                chartRef.current.destroy();
                chartRef.current = null;
            }
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [chartData, yAxesLeft, yAxesRight, options, dimensionColors]);

    useEffect(() => {
        if (!props.getMetrics || !chartData || !chartData.metricsData) return;
        const list = Object.keys(chartData.metricsData).map(key => ({ value: key, title: chartData.metricsData[key].title }));
        props.getMetrics(list);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [chartData]);

    return (
        <div className={`loading-wrapper ${classNameWrapp}`}>
            <canvas
                className={classNameCanvas}
                height={height}
                ref={canvasRef}
            />
            {loading ? <div className="loading-wrapper__item"><Loading/></div> : null}
        </div>
    );
}

ChartLine.propTypes = {
    classNameWrapp: PropTypes.string,
    classNameCanvas: PropTypes.string,
    height: PropTypes.number,
    yAxesLeft: PropTypes.string,
    yAxesRight: PropTypes.string,
    loading: PropTypes.bool,
    chartData: PropTypes.shape({
        labels: PropTypes.array,
        metricsData: PropTypes.object
    }),
    parameters: PropTypes.object,
    getMetrics: PropTypes.func
};

ChartLine.defaultProps = {
    classNameWrapp: '',
    classNameCanvas: '',
    height: 300,
    yAxesLeft: 'PublisherCreditTotalAmountConfirmed',
    yAxesRight: '',
    loading: false,
    chartData: {
        labels: [],
        metricsData: {}
    }
};

export default ChartLine;
